import { Button } from './button';
import { X } from './icons';
import { Dialog } from '@base-ui/react/dialog';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

let openSheets = 0;
let lockedScrollY = 0;
let originalBodyTop = '';

export default function Modal({ open, onClose, title, eyebrow, children, closeDisabled = false }: { open: boolean; onClose: () => void; title: string; eyebrow?: string; children: ReactNode; closeDisabled?: boolean }) {
  const { t } = useTranslation();
  const titleId = useId();
  const sheetRef = useRef<HTMLDivElement>(null);
  const [sheetElement, setSheetElement] = useState<HTMLDivElement | null>(null);
  const connectSheet = useCallback((element: HTMLDivElement | null) => {
    sheetRef.current = element;
    setSheetElement(element);
  }, []);
  const headerRef = useRef<HTMLElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(document.activeElement instanceof HTMLElement ? document.activeElement : null);
  const closeRef = useRef(onClose);
  const dragStart = useRef(0);
  const dragDistance = useRef(0);

  useEffect(() => { closeRef.current = onClose; }, [onClose]);

  useLayoutEffect(() => {
    const sheet = sheetElement;
    const header = headerRef.current;
    if (!open || !sheet || !header) return;
    const px = (value: string) => Number.parseFloat(value) || 0;
    const reserveHeader = () => {
      const sheetStyle = getComputedStyle(sheet);
      const headerStyle = getComputedStyle(header);
      const height = header.offsetHeight + px(headerStyle.top)
        + px(headerStyle.marginBottom) + px(sheetStyle.paddingTop) + px(sheetStyle.borderTopWidth);
      // Translated titles can wrap. Focused fields must stay below the entire
      // sticky heading and drag handle, rather than a fixed guessed offset.
      sheet.style.setProperty('--sheet-sticky-header-height', `${height}px`);
      sheet.style.setProperty('--sheet-header-top-cover', `${px(headerStyle.top) + px(sheetStyle.paddingTop)}px`);
      sheet.style.setProperty('--sheet-header-gap', headerStyle.marginBottom);
      sheet.style.setProperty('--sheet-pad-right', sheetStyle.paddingRight);
      sheet.style.setProperty('--sheet-pad-left', sheetStyle.paddingLeft);
    };
    reserveHeader();
    const observer = new ResizeObserver(reserveHeader);
    observer.observe(header);
    observer.observe(sheet);
    return () => observer.disconnect();
  }, [open, sheetElement]);

  useEffect(() => {
    if (open) return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const rememberFocus = (event: FocusEvent) => {
      // React autofocus fires during the opening commit, before this listener is removed.
      if (!(event.target instanceof HTMLElement)) return;
      const ownDialog = event.target.closest('[role="dialog"]')?.getAttribute('aria-labelledby')?.split(' ').includes(titleId);
      if (!ownDialog) previousFocusRef.current = event.target;
    };
    document.addEventListener('focusin', rememberFocus);
    return () => document.removeEventListener('focusin', rememberFocus);
  }, [open, titleId]);

  useEffect(() => {
    if (!open || !sheetElement) return;
    const frame = window.requestAnimationFrame(() => {
      if (!sheetElement.contains(document.activeElement)) sheetElement.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, sheetElement]);

  useEffect(() => {
    if (!open) return;
    const previousFocus = previousFocusRef.current;
    if (openSheets++ === 0) {
      lockedScrollY = window.scrollY;
      originalBodyTop = document.body.style.top;
      document.body.classList.add('modal-open');
      document.body.style.top = `-${lockedScrollY}px`;
    }
    return () => {
      if (--openSheets === 0) {
        document.body.classList.remove('modal-open');
        document.body.style.top = originalBodyTop;
        window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
      }
      window.requestAnimationFrame(() => {
        const dialogs = document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]');
        const current = dialogs[dialogs.length - 1];
        if (previousFocus?.isConnected && (!current || current.contains(previousFocus))) previousFocus.focus({ preventScroll: true });
      });
    };
  }, [open]);

  function dragSheet(event: ReactPointerEvent<HTMLButtonElement>) {
    if (event.pointerType === 'mouse') return;
    dragStart.current = event.clientY;
    dragDistance.current = 0;
    event.currentTarget.setPointerCapture(event.pointerId);
    sheetRef.current?.classList.add('is-dragging');
  }

  function moveSheet(event: ReactPointerEvent<HTMLButtonElement>) {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    dragDistance.current = Math.max(0, event.clientY - dragStart.current);
    sheetRef.current?.style.setProperty('--sheet-drag-y', `${dragDistance.current}px`);
  }

  function releaseSheet(event: ReactPointerEvent<HTMLButtonElement>) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    sheetRef.current?.classList.remove('is-dragging');
    if (dragDistance.current > 86 && !closeDisabled) closeRef.current();
    sheetRef.current?.style.removeProperty('--sheet-drag-y');
    dragDistance.current = 0;
  }

  if (!open) return null;
  return <Dialog.Root open={open} modal="trap-focus" disablePointerDismissal={closeDisabled} onOpenChange={(next) => { if (!next && !closeDisabled) closeRef.current(); }}>
    <Dialog.Portal><Dialog.Backdrop forceRender className="modal-backdrop" role="presentation">
      <Dialog.Popup render={<section />} ref={connectSheet} className="modal-sheet" aria-modal="true" aria-labelledby={titleId} initialFocus={false} finalFocus={false} tabIndex={-1}>
        <Button variant="ghost" size="bare" type="button" disabled={closeDisabled} className="drag-handle" aria-label={t('common.close')} onClick={() => closeRef.current()} onPointerDown={dragSheet} onPointerMove={moveSheet} onPointerUp={releaseSheet} onPointerCancel={releaseSheet}><span /></Button>
        <header ref={headerRef}>{eyebrow && <p className="eyebrow">{eyebrow}</p>}<Dialog.Title render={<h2 />} id={titleId}>{title}</Dialog.Title><Dialog.Close render={<Button variant="outline" size="bare" type="button" />} disabled={closeDisabled} className="icon-button" aria-label={t('common.close')}><X /></Dialog.Close></header>{children}
      </Dialog.Popup>
    </Dialog.Backdrop></Dialog.Portal>
  </Dialog.Root>;
}
